import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Psychotest from './pages/Psychotest';
import Exam from './pages/Exam';
import Result from './pages/Result';
import RecruiterDashboard from './pages/RecruiterDashboard';
import QuestionManager from './pages/QuestionManager';
import PsychotestManager from './pages/PsychotestManager';
import './index.css';

const resolveHomeRoute = (user) => {
  if (!user) return '/login';
  if (user.role === 'admin') return '/admin';
  if (user.role === 'recruiter') return '/recruiter';
  return '/dashboard';
};

const PrivateRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <div className="loading-screen"><div className="spinner"></div></div>;
  if (user) return <Navigate to={resolveHomeRoute(user)} replace />;
  return children;
};

const CandidateProtection = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const protectedPaths = ['/psychotest', '/exam'];
    const isProtected = protectedPaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

    if (!isProtected) return undefined;

    document.body.classList.add('protected-exam-page');

    const blockContextMenu = (event) => {
      event.preventDefault();
    };

    const blockDragStart = (event) => {
      event.preventDefault();
    };

    const blockSelectStart = (event) => {
      event.preventDefault();
    };

    const blockCopyActions = (event) => {
      event.preventDefault();
    };

    const blockInspectShortcuts = (event) => {
      const key = String(event.key || '').toLowerCase();
      const ctrlOrCmd = event.ctrlKey || event.metaKey;

      if (
        key === 'f12' ||
        (ctrlOrCmd && event.shiftKey && ['i', 'j', 'c'].includes(key)) ||
        (ctrlOrCmd && ['u', 's', 'p', 'a', 'c', 'x'].includes(key))
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('contextmenu', blockContextMenu, true);
    window.addEventListener('keydown', blockInspectShortcuts, true);
    window.addEventListener('dragstart', blockDragStart, true);
    window.addEventListener('selectstart', blockSelectStart, true);
    window.addEventListener('copy', blockCopyActions, true);
    window.addEventListener('cut', blockCopyActions, true);

    return () => {
      document.body.classList.remove('protected-exam-page');
      window.removeEventListener('contextmenu', blockContextMenu, true);
      window.removeEventListener('keydown', blockInspectShortcuts, true);
      window.removeEventListener('dragstart', blockDragStart, true);
      window.removeEventListener('selectstart', blockSelectStart, true);
      window.removeEventListener('copy', blockCopyActions, true);
      window.removeEventListener('cut', blockCopyActions, true);
    };
  }, [location.pathname]);

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Navigate to={resolveHomeRoute(user)} replace />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/dashboard" element={
        <PrivateRoute role="candidate"><Dashboard /></PrivateRoute>
      } />
      <Route path="/psychotest" element={
        <PrivateRoute role="candidate"><Psychotest /></PrivateRoute>
      } />
      <Route path="/exam/:session_id" element={
        <PrivateRoute role="candidate"><Exam /></PrivateRoute>
      } />
      <Route path="/result/:session_id" element={
        <PrivateRoute role="candidate"><Result /></PrivateRoute>
      } />
      <Route path="/recruiter" element={
        <PrivateRoute role="recruiter"><RecruiterDashboard /></PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute role="admin"><RecruiterDashboard /></PrivateRoute>
      } />
      <Route path="/recruiter/questions" element={
        <PrivateRoute role="recruiter"><QuestionManager /></PrivateRoute>
      } />
      <Route path="/recruiter/psychotest" element={
        <PrivateRoute role="recruiter"><PsychotestManager /></PrivateRoute>
      } />
      <Route path="*" element={<Navigate to={resolveHomeRoute(user)} replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <CandidateProtection>
          <AppRoutes />
        </CandidateProtection>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
